// Venue-search VS1.6: the optional house-rules document in the venue editor.
// One file. The browser uploads it; the server makes it a PDF/A; the venue's
// normal Save stores it (venueFacts.js, houseRulesWrite). Checks and words:
// houseRules.js, src/content/venueEditor.js.
import { lazy, Suspense, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import styled from 'styled-components';

import copy from '../../../content/venueEditor';
import { houseRulesLink, uploadHouseRules } from '../../../firebase/venues';
import { getFormSession, setFormValue, setHouseRulesBusy } from '../../../store/hosted-venue';
import { colors, Field, FormLabel } from '../../styles';
import { ErrorText, Help } from './editorStyles';
import { ACCEPT, errorKey, fileProblem, kindOf, randomUploadName } from './houseRules';
import { cleanName, shrinkPhoto } from './photoUpload';

const PdfViewer = lazy(() => import(/* webpackChunkName: "pdf-viewer" */ '../../Shared/PdfViewer'));

const words = copy.houseRules;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 8px;
`;

const FileName = styled.span`
  font-size: 14px;
  font-weight: 500;
  overflow-wrap: anywhere;
`;

const Button = styled.button`
  font-weight: 500;
  font-size: 14px;
  border-radius: 4px;
  padding: 8px 16px;
  color: ${colors.primaryButton};
  background: transparent;
  border: 1px solid ${colors.primaryButton};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 6px 0;
  font-size: 14px;
  color: ${colors.primaryButton};
  text-decoration: underline;
  cursor: pointer;
`;

const Progress = styled.progress`
  width: 160px;
  height: 8px;
  accent-color: ${colors.primaryButton};
`;

const Status = styled.span`
  font-size: 14px;
  color: ${colors.fadedText};
`;

export const HouseRulesUpload = ({ value }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const session = useSelector(getFormSession);
  const mounted = useRef(true);
  const inputId = useId();
  const inputRef = useRef(null);
  const [step, setStep] = useState(null); // null | 'uploading' | 'converting'
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const closeViewer = useCallback(() => setViewUrl(null), []);
  const busy = step !== null;

  useEffect(() => {
    dispatch(setHouseRulesBusy(busy));
  }, [busy, dispatch]);
  // Leaving the editor mid-upload must not block the next venue's Save.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      dispatch(setHouseRulesBusy(false));
    };
  }, [dispatch]);
  // A result that comes back after the host left, or opened another venue, is dropped.
  const stillHere = (started) => mounted.current && getFormSession(store.getState()) === started;

  const onPick = async (e) => {
    const picked = e.target.files && e.target.files[0];
    e.target.value = ''; // the same file can be picked again after an error
    if (!picked) return;
    setError(null);
    const problem = fileProblem(picked);
    if (problem) {
      setError(problem);
      return;
    }
    setProgress(0);
    setStep('uploading');
    const started = session;
    try {
      const kind = kindOf(picked);
      // A photo becomes an upright JPEG with no GPS (the venue photo code).
      const file = kind.kind === 'photo' ? await shrinkPhoto(picked) : picked;
      const ext = kind.kind === 'photo' ? 'jpg' : kind.ext;
      const path = await uploadHouseRules({
        file,
        name: randomUploadName(ext),
        onProgress: setProgress,
        onConverting: () => setStep('converting')
      });
      if (stillHere(started)) dispatch(setFormValue({ houseRules: { path, fileName: picked.name, isNew: true } }));
    } catch (err) {
      if (stillHere(started)) setError(err && err.code ? errorKey(err) : 'unreadable');
    } finally {
      if (mounted.current) setStep(null);
    }
  };

  // VS1.6 (Braeden): the PDF opens on the page, full screen, so the host never leaves the editor.
  const onView = async () => {
    setError(null);
    try {
      setViewUrl(await houseRulesLink(value.path));
    } catch (err) {
      setError('network');
    }
  };

  return (
    <Field>
      <FormLabel htmlFor={inputId}>{words.label}</FormLabel>
      <Help>{words.help}</Help>
      <Help>{words.googleDocs}</Help>
      <input id={inputId} ref={inputRef} type="file" accept={ACCEPT} style={{ display: 'none' }} onChange={onPick} />
      <Row>
        {busy && (
          <>
            <Status role="status">{step === 'uploading' ? words.uploading : words.converting}</Status>
            {step === 'uploading' && <Progress max="1" value={progress} aria-label={words.uploading} />}
          </>
        )}
        {!busy && value && (
          <>
            <FileName>{value.fileName ? cleanName(value.fileName) : words.saved}</FileName>
            <LinkButton type="button" onClick={onView}>
              {words.view}
            </LinkButton>
            <LinkButton type="button" onClick={() => inputRef.current.click()}>
              {words.replace}
            </LinkButton>
            <LinkButton
              type="button"
              onClick={() => {
                setError(null);
                dispatch(setFormValue({ houseRules: null }));
              }}
            >
              {words.remove}
            </LinkButton>
          </>
        )}
        {!busy && !value && (
          <Button type="button" onClick={() => inputRef.current.click()}>
            {words.upload}
          </Button>
        )}
      </Row>
      {error && <ErrorText role="alert">{words.errors[error]}</ErrorText>}
      {viewUrl && (
        <Suspense fallback={null}>
          <PdfViewer url={viewUrl} words={words.viewer} onClose={closeViewer} />
        </Suspense>
      )}
    </Field>
  );
};
